/**
 * VirtualPhotoStrip - Virtual scrolling for horizontal photo filmstrip (Edit page)
 * 
 * Only renders photos that are visible in the horizontal viewport plus a buffer.
 * Optimized for 1D horizontal scrolling with auto-scroll to active photo.
 * 
 * Now supports lazy loading: Preloads full photo data for visible photos,
 * enabling instant photo switching on the Edit page.
 */

import { PhotoStatusOverlay } from './photo_status_overlay.js';

export class VirtualPhotoStrip {
  constructor(container, options = {}) {
    this.container = container;
    this.scrollContainer = options.scrollContainer || container;
    
    // Strip configuration
    this.itemWidth = options.itemWidth || 96; // w-20 (80px) + padding + ring
    this.itemGap = options.itemGap || 8; // gap-2 (8px)
    this.overscanItems = options.overscanItems || 10; // Render 10 extra items on each side
    
    // Lazy loading
    this.viewportPhotoLoader = options.viewportPhotoLoader || null;
    this.photoService = options.photoService || null;
    this.lastLoadedRange = { start: -1, end: -1 }; // Track last loaded range to avoid duplicate requests
    
    // State management
    this.folderStateManager = options.folderStateManager || null;
    this.editorState = options.editorState || null;
    this.currentFolderId = null;
    
    // Data
    this.photos = []; // Basic photo metadata (id, filename, thumb, processing_status)
    this.currentPhoto = null;
    this.selectedPhotos = new Set();
    this.activePhoto = null;
    
    // Viewport state
    this.visibleRange = { start: 0, end: 0 };
    this.renderedItems = new Map(); // photoId -> element
    
    // Performance
    this.renderScheduled = false;
    this.resizeObserver = null;
    
    this.setupContainer();
    this.setupScrollListener();
    this.setupEventListeners();
  }
  
  /**
   * Setup event listeners for reactive updates
   */
  setupEventListeners() {
    if (this.folderStateManager) {
      // Listen to folder state manager for new folder states
      this.folderStateManager.addEventListener('folderStateCreated', (e) => {
        this.attachFolderStateListeners(e.detail.folderState);
      });
      
      // Attach to existing folder states
      this.folderStateManager.getAllFolderStates().forEach(folderState => {
        this.attachFolderStateListeners(folderState);
      });
    }
    
    if (this.editorState) {
      // Listen for selection changes
      this.editorState.addEventListener('selectedPhotoIds', (e) => {
        // Update selection state and re-render visible items
        this.selectedPhotos = e.detail.value || new Set();
        this.updateVisibleSelectionState();
      });
      
      // Listen for active photo changes (updates both activePhoto and currentPhoto)
      this.editorState.addEventListener('activePhotoId', (e) => {
        const state = this.editorState.get();
        this.activePhoto = state.activePhotoId ? { id: state.activePhotoId } : null;
        this.currentPhoto = state.activePhotoId ? { id: state.activePhotoId } : null;
        this.updateVisibleSelectionState();
      });
    }
  }
  
  /**
   * Update selection state for all currently rendered items
   */
  updateVisibleSelectionState() {
    this.renderedItems.forEach((element, photoId) => {
      const photo = this.photos.find(p => p.id === photoId);
      const index = this.photos.findIndex(p => p.id === photoId);
      if (photo && index !== -1) {
        this.updatePhotoElement(element, photo, index);
      }
    });
  }
  
  /**
   * Attach listeners to a specific FolderState
   */
  attachFolderStateListeners(folderState) {
    // When a photo is updated, efficiently update just that photo's element
    folderState.addEventListener('photoUpdated', (e) => {
      // Only update if this is the currently displayed folder
      if (this.currentFolderId !== folderState.getId()) return;
      
      const { photo } = e.detail;
      const index = this.photos.findIndex(p => p.id === photo.id);
      
      if (index !== -1) {
        // Update photo data in our array
        this.photos[index] = { ...this.photos[index], ...photo };
        
        // If this photo is currently rendered, update its element
        const element = this.renderedItems.get(photo.id);
        if (element) {
          this.updatePhotoElement(element, this.photos[index], index);
        }
      }
    });
  }
  
  setupContainer() {
    // Ensure scroll container has proper styling
    if (this.scrollContainer) {
      // Don't override existing scroll behavior, just ensure positioning
      if (!this.scrollContainer.style.position || this.scrollContainer.style.position === 'static') {
        this.scrollContainer.style.position = 'relative';
      }
    }
    
    // Strip container should be positioned for absolute children
    this.container.style.position = 'relative';
    this.container.style.display = 'block'; // Override flex to use absolute positioning
    this.container.style.minWidth = '0'; // Will be set dynamically
    this.container.style.height = '96px'; // Fixed height for horizontal strip
  }
  
  setupScrollListener() {
    if (!this.scrollContainer) return;
    
    this.scrollContainer.addEventListener('scroll', () => {
      this.scheduleRender();
    }, { passive: true });
  }
  
  /**
   * Update with new photo data
   */
  update(photos, currentPhoto, selectedPhotos, activePhoto, smoothScroll = false, folderId = null) {
    this.photos = photos || [];
    this.currentPhoto = currentPhoto;
    this.selectedPhotos = selectedPhotos || new Set();
    this.activePhoto = activePhoto;
    this.currentFolderId = folderId;
    
    // Force update to re-render existing items with new selection state
    this.forceUpdate = true;
    
    this.updateLayout();
    this.scheduleRender();
    
    // Auto-scroll to current photo
    if (currentPhoto && smoothScroll) {
      setTimeout(() => this.scrollToPhoto(currentPhoto.id, 'smooth'), 100);
    } else if (currentPhoto) {
      setTimeout(() => this.scrollToPhoto(currentPhoto.id, 'auto'), 0);
    }
  }
  
  /**
   * Calculate strip layout (total width)
   */
  updateLayout() {
    // Calculate total width
    const totalWidth = this.photos.length * (this.itemWidth + this.itemGap) - this.itemGap;
    this.container.style.minWidth = `${totalWidth}px`;
  }
  
  /**
   * Calculate which photos should be visible
   */
  calculateVisibleRange() {
    if (!this.scrollContainer || this.photos.length === 0) {
      return { start: 0, end: 0 };
    }
    
    const scrollLeft = this.scrollContainer.scrollLeft;
    const viewportWidth = this.scrollContainer.clientWidth;
    const itemTotalWidth = this.itemWidth + this.itemGap;
    
    // Calculate visible items
    const firstVisibleIndex = Math.floor(scrollLeft / itemTotalWidth);
    const lastVisibleIndex = Math.ceil((scrollLeft + viewportWidth) / itemTotalWidth);
    
    // Add overscan
    const start = Math.max(0, firstVisibleIndex - this.overscanItems);
    const end = Math.min(this.photos.length, lastVisibleIndex + this.overscanItems);
    
    return { start, end };
  }
  
  /**
   * Schedule a render (debounced with requestAnimationFrame)
   */
  scheduleRender() {
    if (this.renderScheduled) return;
    
    this.renderScheduled = true;
    requestAnimationFrame(() => {
      this.renderScheduled = false;
      this.render();
    });
  }
  
  /**
   * Render visible items
   */
  render() {
    const newRange = this.calculateVisibleRange();
    
    // Skip if range hasn't changed AND we're not forcing an update
    if (newRange.start === this.visibleRange.start && 
        newRange.end === this.visibleRange.end &&
        this.renderedItems.size > 0 &&
        !this.forceUpdate) {
      return;
    }
    
    // Clear force update flag
    this.forceUpdate = false;
    
    this.visibleRange = newRange;
    
    // Preload full photo data for visible range (async, non-blocking)
    this.preloadPhotosInRange(newRange.start, newRange.end);
    
    // Get currently rendered photo IDs
    const currentIds = new Set(this.renderedItems.keys());
    const newIds = new Set(
      this.photos.slice(newRange.start, newRange.end).map(p => p.id)
    );
    
    // Remove items that are no longer visible
    currentIds.forEach(id => {
      if (!newIds.has(id)) {
        const element = this.renderedItems.get(id);
        if (element && element.parentElement) {
          element.remove();
        }
        this.renderedItems.delete(id);
      }
    });
    
    // Add/update visible items
    for (let i = newRange.start; i < newRange.end; i++) {
      const photo = this.photos[i];
      if (!photo) continue;
      
      let element = this.renderedItems.get(photo.id);
      
      if (!element) {
        // Create new element
        element = this.createPhotoElement(photo, i);
        this.container.appendChild(element);
        this.renderedItems.set(photo.id, element);
      } else {
        // Update existing element (position and state)
        this.updatePhotoElement(element, photo, i);
      }
    }
  }
  
  /**
   * Preload full photo data for visible range
   * This makes switching between photos instant (data already loaded)
   */
  async preloadPhotosInRange(startIndex, endIndex) {
    if (!this.viewportPhotoLoader || this.photos.length === 0) {
      return;
    }
    
    // Skip if we've already loaded this exact range
    if (startIndex === this.lastLoadedRange.start && endIndex === this.lastLoadedRange.end) {
      return;
    }
    
    this.lastLoadedRange = { start: startIndex, end: endIndex };
    
    try {
      // Calculate which photos need loading (visible range only, no extra buffer needed for strip)
      // The overscanItems already provides enough buffer
      const photoIdsToLoad = this.viewportPhotoLoader.calculatePhotosToLoad(
        this.photos,
        startIndex,
        endIndex,
        this.currentPhoto?.id
      );
      
      if (photoIdsToLoad.length > 0) {
        console.log(`[VirtualPhotoStrip] Preloading ${photoIdsToLoad.length} photos for range ${startIndex}-${endIndex}`);
        // Use smaller batch size for filmstrip (20 vs 50 for grid)
        await this.viewportPhotoLoader.loadPhotos(photoIdsToLoad, 20);
      }
    } catch (error) {
      console.error('[VirtualPhotoStrip] Error preloading photos:', error);
    }
  }
  
  /**
   * Create a photo strip item element
   */
  createPhotoElement(photo, index) {
    const thumbUrl = photo.thumb_256_url || photo.thumb_1024_url;
    
    if (!thumbUrl) {
      console.warn('No image URL for photo:', photo);
      return document.createElement('div');
    }
    
    const isCurrent = this.currentPhoto?.id === photo.id;
    const isSelected = this.selectedPhotos.has(photo.id);
    const isActive = this.activePhoto?.id === photo.id;
    
    // Calculate position
    const left = index * (this.itemWidth + this.itemGap);
    
    // Wrapper to prevent layout shift (padding accommodates borders/scaling)
    const wrapper = document.createElement('div');
    wrapper.className = 'flex-shrink-0 p-1 absolute flex items-center justify-center';
    wrapper.style.left = `${left}px`;
    wrapper.style.width = `${this.itemWidth}px`;
    wrapper.style.height = '100%';
    wrapper.dataset.photoId = photo.id;
    wrapper.dataset.index = index;
    
    const div = document.createElement('div');
    div.className = 'relative cursor-pointer transition-all rounded w-20 h-20 bg-gray-700';
    
    // Apply styling based on state - make selections VERY obvious
    if (isCurrent) {
      div.className += ' ring-4 ring-white scale-110 shadow-xl';
    } else if (isActive) {
      div.className += ' ring-4 ring-blue-500 scale-105 shadow-lg hover:shadow-xl';
    } else if (isSelected) {
      div.className += ' ring-4 ring-blue-400 shadow-md hover:shadow-lg';
    } else {
      div.className += ' opacity-60 hover:opacity-100 hover:ring-2 hover:ring-gray-500';
    }
    
    const img = document.createElement('img');
    img.src = thumbUrl;
    img.alt = photo.original_filename;
    img.className = 'w-full h-full object-cover rounded';
    // Note: No lazy loading needed - virtual scrolling already handles this
    
    div.appendChild(img);
    
    // Add processing status overlay if needed
    const statusOverlay = PhotoStatusOverlay.createOverlayHTML(photo.processing_status);
    if (statusOverlay) {
      div.insertAdjacentHTML('beforeend', statusOverlay);
    }
    
    // Add selection indicators
    if (isCurrent) {
      // Current photo (being edited) - white badge with blue dot
      const currentIndicator = document.createElement('div');
      currentIndicator.className = 'absolute -top-0.5 -right-0.5 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-gray-800 z-10';
      currentIndicator.innerHTML = '<svg class="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16z"></path></svg>';
      div.appendChild(currentIndicator);
    } else if (isActive) {
      // Active photo - blue badge with white dot
      const activeIndicator = document.createElement('div');
      activeIndicator.className = 'absolute top-0.5 right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center shadow-md border border-white z-10';
      activeIndicator.innerHTML = '<svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16z"></path></svg>';
      div.appendChild(activeIndicator);
    } else if (isSelected) {
      // Selected photo - blue badge with white checkmark
      const checkmark = document.createElement('div');
      checkmark.className = 'absolute -top-0.5 -right-0.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10';
      checkmark.innerHTML = '<svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"></path></svg>';
      div.appendChild(checkmark);
    }
    
    wrapper.appendChild(div);
    return wrapper;
  }
  
  /**
   * Update an existing photo element
   */
  updatePhotoElement(wrapper, photo, index) {
    // Update position
    const left = index * (this.itemWidth + this.itemGap);
    wrapper.style.left = `${left}px`;
    wrapper.dataset.index = index;
    
    // Ensure wrapper has correct classes
    wrapper.className = 'flex-shrink-0 p-1 absolute flex items-center justify-center';
    
    // Update selection state
    const div = wrapper.querySelector('.relative');
    if (!div) return;
    
    const isCurrent = this.currentPhoto?.id === photo.id;
    const isSelected = this.selectedPhotos.has(photo.id);
    const isActive = this.activePhoto?.id === photo.id;
    
    // Update thumbnail image src if it changed
    const img = div.querySelector('img');
    if (img) {
      const thumbUrl = photo.thumb_256_url || photo.thumb_1024_url;
      if (img.src !== thumbUrl) {
        img.src = thumbUrl;
      }
    }
    
    // Update classes (including w-20 h-20 sizing and background)
    div.className = 'relative cursor-pointer transition-all rounded w-20 h-20 bg-gray-700';
    if (isCurrent) {
      div.className += ' ring-4 ring-white scale-110 shadow-xl';
    } else if (isActive) {
      div.className += ' ring-4 ring-blue-500 scale-105 shadow-lg hover:shadow-xl';
    } else if (isSelected) {
      div.className += ' ring-4 ring-blue-400 shadow-md hover:shadow-lg';
    } else {
      div.className += ' opacity-60 hover:opacity-100 hover:ring-2 hover:ring-gray-500';
    }
    
    // Update processing status overlay
    PhotoStatusOverlay.updatePhotoOverlay(div, photo.processing_status);
    
    // Update indicators (remove old ones)
    wrapper.querySelectorAll('.absolute.-top-0\\.5.-right-0\\.5, .absolute.top-0\\.5.right-0\\.5').forEach(el => el.remove());
    
    if (isCurrent) {
      const currentIndicator = document.createElement('div');
      currentIndicator.className = 'absolute -top-0.5 -right-0.5 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-gray-800 z-10';
      currentIndicator.innerHTML = '<svg class="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16z"></path></svg>';
      div.appendChild(currentIndicator);
    } else if (isActive) {
      const activeIndicator = document.createElement('div');
      activeIndicator.className = 'absolute top-0.5 right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center shadow-md border border-white z-10';
      activeIndicator.innerHTML = '<svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16z"></path></svg>';
      div.appendChild(activeIndicator);
    } else if (isSelected) {
      const checkmark = document.createElement('div');
      checkmark.className = 'absolute -top-0.5 -right-0.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10';
      checkmark.innerHTML = '<svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"></path></svg>';
      div.appendChild(checkmark);
    }
  }
  
  /**
   * Scroll to center a specific photo
   */
  scrollToPhoto(photoId, behavior = 'auto') {
    const index = this.photos.findIndex(p => p.id === photoId);
    if (index === -1) return;
    
    const left = index * (this.itemWidth + this.itemGap);
    const containerWidth = this.scrollContainer.clientWidth;
    const scrollLeft = left - (containerWidth / 2) + (this.itemWidth / 2);
    
    if (this.scrollContainer) {
      this.scrollContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior
      });
    }
  }
  
  /**
   * Get photo at a specific point (for click handling)
   */
  getPhotoAtPoint(x, y) {
    // Check rendered items first (fast path)
    for (const [photoId, element] of this.renderedItems) {
      const rect = element.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return this.photos.find(p => p.id === photoId);
      }
    }
    return null;
  }
  
  /**
   * Clean up
   */
  destroy() {
    this.renderedItems.clear();
    this.container.innerHTML = '';
  }
}

